import { Outlet, useNavigate } from "react-router";
import { useEffect, useState } from "react";
import { DashboardShell } from "@/components/layout/dashboard-shell";
import { bootstrapSession } from "@/lib/auth/session-store";
import { useAppContext } from "@/providers";
import { cn } from "@/lib/utils";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { MetaTags } from "@/components/seo/meta-tags";
import { t } from "@/lib/i18n/messages";

export function DashboardLayout() {
  const navigate = useNavigate();
  const { setSession, locale } = useAppContext();
  const [unauthMessage, setUnauthMessage] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);

  useDocumentTitle(`${t(locale, "navOverview")} | NCircle`);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<string>).detail;
      setUnauthMessage(detail || "Session expired");
      void navigate("/login", { replace: true });
    };
    window.addEventListener("athr:unauthorized", handler);

    void (async () => {
      const session = await bootstrapSession();
      setSession(session);
      if (!session.isAuthenticated) {
        window.dispatchEvent(
          new CustomEvent("athr:unauthorized", {
            detail: session.errorMessage || "Session expired",
          })
        );
        void navigate("/login", { replace: true });
      }
      setChecked(true);
    })();

    return () => {
      window.removeEventListener("athr:unauthorized", handler);
    };
  }, [navigate, setSession]);

  if (!checked) return null;

  return (
    <>
      <MetaTags robots="noindex, nofollow" />
      {unauthMessage && (
        <div
          className={cn(
            "fixed inset-x-0 top-0 z-[60] px-4 py-2 text-center text-sm font-medium",
            "bg-error text-error-foreground animate-slide-in-top"
          )}
          role="alert"
        >
          {unauthMessage}
        </div>
      )}
      <DashboardShell>
        <Outlet />
      </DashboardShell>
    </>
  );
}
